import { type FC } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { skipToken } from '@reduxjs/toolkit/query'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetBookingByIdQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
  useRescheduleBookingMutation,
} from '../../../store/scheduling/scheduling'
import { useGetUserByIdQuery } from '../../../store/user/user'
import { type Booking, type Location, LocationKind, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { bookingLocationFields, canManageOwnLocations } from './form/scheduleHelpers'
import { toLocalISODate } from '../../../common/dateUtils'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { formatBookingDate, formatTimeRange } from '../bookingFormat'
import { ContactChips } from '../rows/ContactChips'
import { EventTypeChip } from '../rows/EventTypeChip'
import { ProviderBadge } from '../rows/ProviderBadge'
import { LocationSelect } from './form/LocationSelect'
import { SetTimeSection } from './form/SetTimeSection'
import { PlaceToggleCards } from './form/PlaceToggleCards'
import { SchedulePreviewColumn } from './SchedulePreviewColumn'
import { SchedulingFlowHeader } from './SchedulingFlowHeader'
import { ScheduleSaveBar } from './ScheduleSaveBar'
import { useBookingLinkActions } from './useBookingLinkActions'
import { useCopyInvitationEmail } from './useCopyInvitationEmail'
import { formatTimezoneLabel } from '../shared/schedulingDisplay'
import { scheduleFormSchema, type ScheduleFormValues } from './scheduleFormSchema'
import { useDocumentTitle } from '../../../common/useDocumentTitle'

const FORM_ID = 'reschedule-meeting-form'

interface RescheduleMeetingFormProps {
  booking: Booking
  eventType?: SchedulingEventType
  ownerName: string
  accountLocations: Location[]
  accountDefaultLocationId?: string
  allowCustomLocation: boolean
  bookingPageSlug?: string
}

const RescheduleMeetingForm: FC<RescheduleMeetingFormProps> = ({
  booking,
  eventType,
  ownerName,
  accountLocations,
  accountDefaultLocationId,
  allowCustomLocation,
  bookingPageSlug,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [rescheduleBooking, { isLoading: isSending }] = useRescheduleBookingMutation()
  const { onCopyLink, onPushToDraft } = useBookingLinkActions(bookingPageSlug)
  const copyInvitationEmail = useCopyInvitationEmail()

  const onCopyEmail = async () => {
    const link = booking.clients.find((candidate) => candidate.inviteUrl)?.inviteUrl
    if (!eventType || !link) {
      dispatch(addToast({ variant: 'error', title: 'No booking link available', messages: ['This booking doesn’t have a shareable link.'] }))
      return
    }
    await copyInvitationEmail({ eventType, client: booking.clients[0], ownerName, bookingUrl: link })
  }

  const getUserQuery = useGetUserByIdQuery({ accountId, userId: booking.userId })

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      owner: getUserQuery.data?.data,
      eventType: eventType ?? null,
      clients: booking.clients,
      timeMode: 'specific-time',
      slot: null,
      suggestedDate: null,
      meetingMode: booking.meetingMode ?? MeetingMode.VIRTUAL,
      location: booking.location ?? null,
      note: '',
      title: booking.title ?? eventType?.name ?? '',
      titleEdited: true,
      invitees: booking.additionalInvitees ?? [],
      timezone: userTimezone,
      customLocation: null,
    },
  })

  const { timeMode, slot, suggestedDate, meetingMode, note, location: locationRef, title, timezone, invitees } = watch()
  const validate = { shouldValidate: true, shouldDirty: true }

  const { data: userLocations } = useGetUserSchedulingLocationsQuery({ accountId, userId: booking.userId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId: booking.userId })

  const allLocations = [...accountLocations, ...(userLocations ?? [])]
  const selectedSavedLocation = locationRef?.id ? allLocations.find((location) => location.id === locationRef.id) : undefined
  const effectiveLocationTimezone = selectedSavedLocation?.timeZone || ''
  const locationTimezoneLabel = effectiveLocationTimezone ? formatTimezoneLabel(effectiveLocationTimezone) : ''

  const onSend = handleSubmit(async (data) => {
    const result = await rescheduleBooking({
      accountId,
      payload: {
        bookingId: booking.bookingId,
        start: data.timeMode === 'specific-time' ? data.slot?.startAt : undefined,
        suggestedDate: data.timeMode === 'client-chooses' && data.suggestedDate ? toLocalISODate(data.suggestedDate) : undefined,
        ...bookingLocationFields(data.meetingMode, data.location),
        note: data.note || undefined,
      },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not reschedule the meeting', messages: ['Please try again.'] }))
      return
    }
    if (data.timeMode === 'specific-time') {
      dispatch(addToast({ variant: 'success', title: 'Meeting rescheduled', messages: [`${result.data.title ?? 'The meeting'} moved to a new time.`] }))
      navigate('/scheduling/scheduled')
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Reschedule request sent', messages: ['The client can now pick a new time.'] }))
    navigate('/scheduling/waiting')
  })

  return (
    <>
      <Row>
        <Col lg={6} className='d-flex flex-column gap-4'>
          <div className='d-flex flex-column gap-3 p-3 border zocks-border-color-gray-200 zocks-border-1.0 zocks-bg-color-white'>
            {booking.start && booking.end ? (
              <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
                <span className='zocks-text-gray-500'>{formatBookingDate(new Date(booking.start))}</span>
                <span>{formatTimeRange(booking.start, booking.end)}</span>
              </div>
            ) : null}
            <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
              <ContactChips clients={[...booking.clients, ...(booking.additionalInvitees ?? [])]} />
              <span className='zocks-text-gray-600'>·</span>
              <EventTypeChip eventType={eventType} />
              <span className='zocks-text-gray-600'>·</span>
              <ProviderBadge meetingMode={booking.meetingMode} meetingUrl={booking.meetingUrl} />
            </div>
          </div>
          <form id={FORM_ID} className='d-flex flex-column gap-4' onSubmit={onSend}>
            {eventType ? (
              <>
                <SetTimeSection
                  mode={timeMode}
                  eventTypeId={eventType.id}
                  userId={booking.userId}
                  meetingMode={meetingMode}
                  timezone={timezone}
                  locationTimezoneLabel={locationTimezoneLabel}
                  selectedSlot={slot}
                  suggestedDate={suggestedDate}
                  onModeChange={(value) => setValue('timeMode', value, validate)}
                  onSlotChange={(value) => setValue('slot', value, validate)}
                  onSuggestedDateChange={(value) => setValue('suggestedDate', value, validate)}
                  onTimezoneChange={(value) => setValue('timezone', value, validate)}
                />
                <PlaceToggleCards
                  meetingMode={meetingMode}
                  defaultLocation={allLocations.find((location) => location.id === accountDefaultLocationId)}
                  onChange={(nextMode, nextLocationId) => {
                    setValue('meetingMode', nextMode, validate)
                    setValue('location', nextLocationId ? { kind: LocationKind.FIXED, id: nextLocationId } : null, validate)
                  }}
                />
                {meetingMode === MeetingMode.IN_PERSON ? (
                  <LocationSelect
                    eventType={eventType}
                    accountDefaultLocationId={accountDefaultLocationId}
                    userDefaultLocationId={userSettings?.defaults?.locationId}
                    accountLocations={accountLocations}
                    userLocations={userLocations ?? []}
                    ownerUserId={booking.userId}
                    ownerFirstName={ownerName.split(' ')[0]}
                    isSchedulingForSelf={booking.userId === authenticatedUser.id}
                    location={locationRef}
                    customLocation={null}
                    allowCustomLocation={allowCustomLocation}
                    onChange={(next) => {
                      if (next.location !== undefined) {
                        setValue('location', next.location, validate)
                      }
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </form>
        </Col>
        <Col lg={6}>
          <SchedulePreviewColumn
            eventType={eventType}
            mode={timeMode}
            emptyState={
              <div className='d-flex align-items-center justify-content-center text-center p-4 border zocks-border-color-gray-200 zocks-border-1.0 paragraph-medium zocks-text-gray-500'>
                This booking is missing its event type, so it can&apos;t be rescheduled here.
              </div>
            }
            ownerName={ownerName}
            invitees={invitees}
            meetingMode={meetingMode}
            slot={slot}
            title={title}
            timezoneLabel={meetingMode === MeetingMode.IN_PERSON ? locationTimezoneLabel : timezone ? formatTimezoneLabel(timezone) : ''}
            location={selectedSavedLocation ?? null}
            note={note}
            onNoteChange={(value) => setValue('note', value, validate)}
            client={booking.clients[0]}
            onEditEmail={() => navigate('/scheduling/setup')}
            onCopyEmail={() => void onCopyEmail()}
          />
        </Col>
      </Row>
      <ScheduleSaveBar formId={FORM_ID} mode={timeMode} isValid={isValid} isSubmitting={isSending} onCopyLink={onCopyLink} onPushToDraft={onPushToDraft} />
    </>
  )
}

export const RescheduleMeetingFlow: FC = () => {
  const navigate = useNavigate()
  const { bookingId = '' } = useParams()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const bookingQuery = useGetBookingByIdQuery(bookingId ? { accountId, bookingId } : skipToken)
  const eventTypesQuery = useGetSchedulingEventTypesQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  useDocumentTitle('Scheduling | Zocks')

  const booking = bookingQuery.data
  const ownerUserId = booking?.userId
  const isOwnerSelf = ownerUserId === authenticatedUser.id
  const ownerQuery = useGetUserByIdQuery(ownerUserId && !isOwnerSelf ? { accountId, userId: ownerUserId } : skipToken)
  const eventType = booking?.eventTypeId ? eventTypesQuery.data?.find((candidate) => candidate.id === booking.eventTypeId) : undefined
  const ownerName = getUserDisplayName(isOwnerSelf ? authenticatedUser : (ownerQuery.data?.data ?? authenticatedUser), 'Me')
  const isPending = bookingQuery.isLoading || eventTypesQuery.isLoading || ownerQuery.isLoading

  return (
    <Container>
      <SchedulingFlowHeader title='Reschedule a meeting' onBack={() => navigate(-1)} onClose={() => navigate(-1)} />
      {isPending ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : booking ? (
        <RescheduleMeetingForm
          booking={booking}
          eventType={eventType}
          ownerName={ownerName}
          accountLocations={locations ?? []}
          accountDefaultLocationId={accountSettings?.defaults?.locationId}
          allowCustomLocation={canManageOwnLocations(accountSettings?.userControls)}
          bookingPageSlug={accountSettings?.publicBookingPage?.accountSlug}
        />
      ) : (
        <div className='d-flex justify-content-center py-5 paragraph-medium zocks-text-gray-500'>This booking could not be found.</div>
      )}
    </Container>
  )
}
